'use client'

import Link from "next/link"
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Music2, Crown, Sparkles } from "lucide-react"
import { SITE_CONFIG, RAYONS, BOUTIQUE_EVA_DIAW } from "@/lib/site-config"

export function SiteFooter() {
  return (
    <footer className="mt-auto bg-cuir text-primary-foreground border-t-4 border-gold">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full gradient-gold flex items-center justify-center">
                <span className="font-display text-2xl font-bold text-cuir">S</span>
              </div>
              <div>
                <div className="font-display text-2xl font-semibold text-gold tracking-luxe">SADIYA</div>
                <div className="text-[10px] uppercase tracking-luxe-wide text-primary-foreground/70">
                  Style Cuir · Paris
                </div>
              </div>
            </div>
            <p className="text-sm text-primary-foreground/80 leading-[1.7]">
              {SITE_CONFIG.description}
            </p>
            <p className="text-xs text-gold italic tracking-luxe">
              « {SITE_CONFIG.tagline} »
            </p>
            {/* Ornamental divider */}
            <div className="flex items-center gap-2 max-w-[200px]">
              <span className="h-px flex-1 bg-gradient-to-r from-gold/60 to-transparent" />
              <svg viewBox="0 0 16 16" className="h-3 w-3 text-gold/80" fill="none" stroke="currentColor" strokeWidth="0.8">
                <path d="M8 1 L15 8 L8 15 L1 8 Z" />
                <circle cx="8" cy="8" r="1.5" fill="currentColor" />
              </svg>
              <span className="h-px flex-1 bg-gradient-to-l from-gold/60 to-transparent" />
            </div>
          </div>

          {/* Rayons */}
          <div>
            <h3 className="font-display text-lg font-semibold text-gold mb-4 tracking-luxe">Nos Rayons</h3>
            <ul className="space-y-2.5 max-h-72 overflow-y-auto custom-scrollbar">
              {RAYONS.map((r) => (
                <li key={r.slug} className="flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-gold/60 flex-shrink-0" />
                  <Link
                    href={`#rayon-${r.slug}`}
                    className="text-sm text-primary-foreground/80 hover:text-gold transition-colors"
                  >
                    {r.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Maison — Collection & Services */}
          <div>
            <h3 className="font-display text-lg font-semibold text-gold mb-4 tracking-luxe">La Maison</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/collection-privee"
                  className="flex items-start gap-2 text-sm text-primary-foreground/80 hover:text-gold transition-colors group"
                >
                  <Crown className="h-4 w-4 mt-0.5 text-gold flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span>
                    Collection Privilège
                    <span className="block text-[10px] text-primary-foreground/50 italic">
                      Pièces d'exception signées Eva Diaw
                    </span>
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  href="/services-luxe"
                  className="flex items-start gap-2 text-sm text-primary-foreground/80 hover:text-gold transition-colors group"
                >
                  <Sparkles className="h-4 w-4 mt-0.5 text-gold flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span>
                    Services de Luxe
                    <span className="block text-[10px] text-primary-foreground/50 italic">
                      L'art de recevoir selon Eva Diaw
                    </span>
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  href="/boutique-eva-diaw"
                  className="flex items-start gap-2 text-sm text-primary-foreground/80 hover:text-gold transition-colors group"
                >
                  <span className="h-4 w-4 mt-0.5 text-gold flex-shrink-0 text-xs font-bold flex items-center justify-center">E.D</span>
                  <span>
                    Boutique Eva Diaw
                    <span className="block text-[10px] text-primary-foreground/50 italic">
                      Curatrice mode & art de vivre
                    </span>
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-display text-lg font-semibold text-gold mb-4">Contact</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-gold flex-shrink-0" />
                <span className="text-primary-foreground/80">
                  {SITE_CONFIG.address.line1}<br />
                  {SITE_CONFIG.address.line2}<br />
                  {SITE_CONFIG.address.zip} {SITE_CONFIG.address.city}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-gold flex-shrink-0" />
                <a href={`tel:${SITE_CONFIG.contact.phone.replace(/\s/g, "")}`} className="text-primary-foreground/80 hover:text-gold">
                  {SITE_CONFIG.contact.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-gold flex-shrink-0" />
                <a href={`mailto:${SITE_CONFIG.contact.email}`} className="text-primary-foreground/80 hover:text-gold">
                  {SITE_CONFIG.contact.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-4 w-4 mt-0.5 text-gold flex-shrink-0" />
                <span className="text-primary-foreground/80">{SITE_CONFIG.contact.hours}</span>
              </li>
            </ul>
          </div>

          {/* Social + admin */}
          <div>
            <h3 className="font-display text-lg font-semibold text-gold mb-4 tracking-luxe">Suivez-nous</h3>
            <div className="flex gap-3 mb-6">
              <a href={`https://instagram.com/${SITE_CONFIG.social.instagram.replace('@', '')}`}
                 target="_blank" rel="noopener noreferrer"
                 className="h-11 w-11 rounded-full border border-gold/40 hover:border-gold bg-primary-foreground/5 hover:bg-gold hover:text-cuir text-gold flex items-center justify-center transition-all duration-300 hover:scale-105"
                 aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="#" className="h-11 w-11 rounded-full border border-gold/40 hover:border-gold bg-primary-foreground/5 hover:bg-gold hover:text-cuir text-gold flex items-center justify-center transition-all duration-300 hover:scale-105" aria-label="Facebook">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="#" className="h-11 w-11 rounded-full border border-gold/40 hover:border-gold bg-primary-foreground/5 hover:bg-gold hover:text-cuir text-gold flex items-center justify-center transition-all duration-300 hover:scale-105" aria-label="TikTok">
                <Music2 className="h-4 w-4" />
              </a>
            </div>
            <Link href="/admin" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-luxe-wide text-gold/40 hover:text-gold transition-colors">
              <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="1.2">
                <path d="M6 1 L11 6 L6 11 L1 6 Z" />
              </svg>
              Espace Administration
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-primary-foreground/10">
          {/* Partnership banner */}
          <div className="mb-6 p-4 rounded-lg bg-primary-foreground/5 border border-gold/30">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="text-center md:text-left">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1">
                  En collaboration avec
                </div>
                <a
                  href={BOUTIQUE_EVA_DIAW.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-display text-lg font-semibold text-gold hover:text-primary-foreground transition-colors"
                >
                  {BOUTIQUE_EVA_DIAW.name}
                </a>
                <div className="text-xs text-primary-foreground/60 mt-1">
                  {BOUTIQUE_EVA_DIAW.tagline} — {BOUTIQUE_EVA_DIAW.domain}
                </div>
              </div>
              <div className="text-center md:text-right text-xs text-primary-foreground/70 italic max-w-xs">
                « {BOUTIQUE_EVA_DIAW.description.slice(0, 120)}… »
                <div className="mt-1 text-gold">— {BOUTIQUE_EVA_DIAW.signature}</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-primary-foreground/60">
            <p>© {new Date().getFullYear()} {SITE_CONFIG.legalName}. Tous droits réservés.</p>
            <p className="text-gold/80">
              Fondée par {SITE_CONFIG.founder} — {SITE_CONFIG.founderTitle}
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
